import { useEffect, useState } from 'react';
import type { SearchTrade } from 'lib/sdks/search';
import { v3Search } from 'lib/sdks/search';
import { getDeviceId } from 'lib/utils/device';

export const useGetJobLeads = ({
  categoryLabel,
  companyIds,
  postcode,
  options: { numberOfLeads = 4 } = {},
}: {
  categoryLabel?: string;
  companyIds?: number[];
  postcode?: string;
  options?: {
    numberOfLeads?: number;
  };
}) => {
  const [tradeLeads, setTradeLeads] = useState<SearchTrade[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const getJobLeadsResult = async () => {
      setLoading(true);

      if (!categoryLabel || !postcode) {
        setLoading(false);
        return;
      }

      try {
        const searchResponse = await v3Search(
          categoryLabel,
          postcode,
          {
            size: numberOfLeads,
            injections: 'false',
            algorithm: sessionStorage.algorithm
              ? sessionStorage.algorithm.startsWith('LeadsRanking')
                ? `Raq${sessionStorage.algorithm}`
                : sessionStorage.algorithm
              : 'raq',
            excludeCompanyIds: companyIds,
          },
          getDeviceId(),
          signal,
        );

        setTradeLeads(searchResponse?.items || []);
        setLoading(false);
      } catch (e) {
        setTradeLeads([]);
        setLoading(false);
      }
    };

    getJobLeadsResult();

    return () => {
      controller.abort();
    };
    // We use array.toString() to get comma-separated list of numbers.
    // It is possible that subsequent calls made by the parent component
    // will call this hook with companyIds array containing the same values
    // However array instance (reference) will be different.
    // This will re-trigger useEffect and initiate an infinite loop calling search.
    // String representation of an array won't re-trigger useEffect, if ids remain the same
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postcode, categoryLabel, companyIds?.toString()]);

  return [loading, tradeLeads] as const;
};
